import { useCallback, useEffect, useRef, useState } from "react"

import type { DefaultValues, FieldValues, UseFormReturn } from "react-hook-form"
import type { z } from "zod"

export type AutosaveState = "idle" | "saving" | "saved"

const DEBOUNCE_MS = 2000

// Saves usually beat the eye, and a label that flashes reads as a glitch
// rather than as reassurance.
const MIN_SAVING_MS = 600

type DraftValues = Record<string, unknown>

interface Options<T extends FieldValues> {
  form: UseFormReturn<T>
  /**
   * What the draft or real record held when the form opened: the same values
   * handed to `useForm` as `defaultValues`. Later ones arrive via `hydrate`.
   */
  initialValues: DefaultValues<T>
  /** The draft endpoint's body, applied a field at a time via its shape. */
  schema: z.ZodObject<z.ZodRawShape>
  /** The draft endpoint's mutation, with its error toast turned off. */
  save: (values: DraftValues) => Promise<unknown>
  /** False turns autosave off, as once the real record exists. */
  enabled: boolean
  /**
   * Where the draft's fields sit, for a form that nests them. Anything outside
   * it is left alone, which is how a form holding a field the draft has no
   * column for keeps that field to itself.
   */
  prefix?: string
}

interface Autosave<T extends FieldValues> {
  status: AutosaveState
  /**
   * Fills the form from the draft or the real record, and records those values
   * as what the draft holds. Use it in place of `form.reset`, whose events the
   * hook cannot tell apart from the borrower's.
   */
  hydrate: (values: DefaultValues<T>) => void
  /**
   * Drops a pending save and waits out one already sent, which the server
   * processes regardless. Typing re-arms it.
   */
  cancel: () => Promise<void>
}

/**
 * Saves what someone has typed into a draft record, a couple of seconds after
 * they stop.
 *
 * Only changed, individually valid fields are sent, so a form nowhere near
 * complete still saves the parts that are. Opening a form sends nothing:
 * changed means against what the draft is known to hold, not against
 * emptiness.
 *
 * A failed save is silent. Submitting writes the real record whatever autosave
 * managed, so a failure costs the borrower nothing unless they also abandon the
 * page, and there is nothing they could do about it if told.
 */
export function useAutosaveDraft<T extends FieldValues>({
  form,
  initialValues,
  schema,
  save,
  enabled,
  prefix,
}: Options<T>): Autosave<T> {
  const [status, setStatus] = useState<AutosaveState>("idle")

  const draftValues = useCallback(
    (values: FieldValues): DraftValues =>
      prefix ? ((values[prefix] as DraftValues) ?? {}) : values,
    [prefix],
  )

  // What the draft is known to hold, kept apart from what the form holds.
  // Moved on only by `hydrate` and by saves that land.
  const savedRef = useRef<DraftValues>(draftValues(initialValues))

  // Read through a ref, so a caller can hand over an inline function without
  // resubscribing to the form on every render.
  const saveRef = useRef(save)
  saveRef.current = save

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestRef = useRef<Promise<unknown> | null>(null)
  const inFlightRef = useRef(false)
  const queuedRef = useRef(false)

  // Bumped by every cancel. A save carries the value it began under and drops
  // its result once that has moved on, so an abandoned save stores nothing.
  const epochRef = useRef(0)

  const hydrate = useCallback(
    (values: DefaultValues<T>) => {
      // Recorded first: resetting notifies the subscription below, which
      // compares against this.
      savedRef.current = draftValues(values)
      form.reset(values)
    },
    [draftValues, form],
  )

  const cancel = useCallback(async () => {
    epochRef.current += 1
    queuedRef.current = false

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    // A save already sent cannot be recalled, so the only way to keep it from
    // landing after whatever the caller does next is to let it finish first.
    await requestRef.current?.catch(() => {})
    requestRef.current = null
    inFlightRef.current = false

    // The abandoned save never reaches its own setStatus, so Saving would
    // otherwise stand on a form the borrower stays on. Only that state is
    // wrong: an earlier Saved is still true of the draft.
    setStatus((previous) => (previous === "saving" ? "idle" : previous))
  }, [])

  // The changed fields the draft will take, and whether any changed field had
  // to be left out.
  const collectChanges = useCallback(() => {
    const current = draftValues(form.getValues())
    const payload: DraftValues = {}
    let hasUnsaved = false
    // Read once: zod rebuilds the shape object on every access.
    const { shape } = schema

    for (const [name, value] of Object.entries(current)) {
      if (value === savedRef.current[name]) continue

      // Skips a field the draft has no column for.
      const field = shape[name]
      if (!field) continue

      // Holds back a value the draft will not take yet, such as a country
      // chosen but no state. Also an undefined one: it survives validation on
      // an optional field but is dropped from the request body, so sending it
      // would save nothing while recording the field as saved. Clearing a
      // field gives "".
      const parsed = field.safeParse(value)
      if (parsed.success && parsed.data !== undefined) {
        payload[name] = parsed.data
      } else {
        hasUnsaved = true
      }
    }

    return { payload, hasUnsaved }
  }, [draftValues, form, schema])

  const runSave = useCallback(async () => {
    // One at a time: the draft endpoint takes the last write, so two in flight
    // can land out of order.
    if (inFlightRef.current) {
      queuedRef.current = true
      return
    }

    const { payload } = collectChanges()
    if (Object.keys(payload).length === 0) return

    const epoch = epochRef.current
    const request = saveRef.current(payload)
    requestRef.current = request
    inFlightRef.current = true
    const startedAt = Date.now()
    setStatus("saving")

    let saved = false
    try {
      await request
      saved = true
    } catch {
      // Silent: the fields stay out of savedRef, so the next change retries.
    }

    if (epoch !== epochRef.current) return
    requestRef.current = null

    if (saved) savedRef.current = { ...savedRef.current, ...payload }

    const remaining = MIN_SAVING_MS - (Date.now() - startedAt)
    if (remaining > 0) {
      await new Promise((resolve) => setTimeout(resolve, remaining))
      if (epoch !== epochRef.current) return
    }

    inFlightRef.current = false
    // A failure says nothing, and a stale Saved would be worse than silence.
    // So would one shown while a field typed during the save is held back.
    const { hasUnsaved } = collectChanges()
    setStatus(saved && !hasUnsaved ? "saved" : "idle")

    if (queuedRef.current) {
      queuedRef.current = false
      void runSave()
    }
  }, [collectChanges])

  // Every notification is treated the same, since the form also sends them
  // for its own bookkeeping, such as clearing errors. Only what differs from
  // savedRef is ever sent.
  useEffect(() => {
    if (!enabled) return

    const subscription = form.watch(() => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = null

      const { payload, hasUnsaved } = collectChanges()

      // The draft no longer matches the screen, so Saved would be a lie. An
      // in-flight Saving settles itself.
      if (hasUnsaved) {
        setStatus((previous) => (previous === "saved" ? "idle" : previous))
      }

      if (Object.keys(payload).length === 0) return
      timeoutRef.current = setTimeout(() => void runSave(), DEBOUNCE_MS)
    })

    return () => subscription.unsubscribe()
  }, [collectChanges, enabled, form, runSave])

  // Kept apart from the subscription above, which resubscribes whenever its
  // caller hands over new callbacks. Cancelling on that would abandon a save
  // already on its way for no reason; this runs only when autosave stops or
  // the form goes away, which are the two cases that must abandon one.
  useEffect(() => {
    if (!enabled) return
    return () => void cancel()
  }, [cancel, enabled])

  return { status, hydrate, cancel }
}
